import { useEffect, useState } from "react";
import { X, Power, PowerOff, Wind, Zap, Snowflake, Wrench, Gauge } from "lucide-react";
import { FaEdit, FaTrash, FaEye, FaMapMarkerAlt, FaCogs, FaHistory } from "react-icons/fa";
import {
  getAllGroupesFrigorifiques,
  createGroupeFrigorifique,
  updateGroupeFrigorifique,
  deleteGroupeFrigorifique,
  getHistoriqueGroupeFrigorifique
} from "../../services/groupesfrigorifiques";
import { getAllChambresFroides } from "../../services/chambresfroides";
import type { GroupeFrigorifique } from "../../types/groupefrigorifique";
import type { ChambreFroide } from "../../types/chambrefroide";
import type { HistoriqueArretGroupeFrigorifique } from "../../types/historiquearretgroupe";
import "./GroupesFrigorifiques.css";

export default function GroupesFrigorifiques() {
  const [groupes, setGroupes] = useState<GroupeFrigorifique[]>([]);
  const [chambres, setChambres] = useState<ChambreFroide[]>([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [selectedGroupe, setSelectedGroupe] = useState<GroupeFrigorifique | null>(null);
  const [viewGroupe, setViewGroupe] = useState<GroupeFrigorifique | null>(null);

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyGroupe, setHistoryGroupe] = useState<GroupeFrigorifique | null>(null);
  const [historiques, setHistoriques] = useState<HistoriqueArretGroupeFrigorifique[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const safeSetArray = <T,>(setter: React.Dispatch<React.SetStateAction<T[]>>) => (data: unknown) => {
          if (Array.isArray(data)) setter(data as T[]);
          else setter([]);
        };

        const data = await getAllGroupesFrigorifiques();
        safeSetArray<GroupeFrigorifique>(setGroupes)(data);
      } catch (err) {
        console.error("Erreur récupération groupes frigorifiques :", err);
        setGroupes([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchChambres = async () => {
      try {
        const data = await getAllChambresFroides();
        setChambres(data);
      } catch (err) {
        console.error("Erreur récupération chambres froides :", err);
      }
    };
    fetchChambres();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    try {
      if (selectedGroupe) {
        const updated = await updateGroupeFrigorifique(selectedGroupe.id, formData);
        setGroupes(groupes.map((g) => (g.id === selectedGroupe.id ? updated : g)));
      } else {
        const newGroupe = await createGroupeFrigorifique(formData);
        setGroupes([...groupes, newGroupe]);
      }
      setShowModal(false);
      setSelectedGroupe(null);
    } catch (err) {
      console.error("Erreur enregistrement groupe :", err);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteGroupeFrigorifique(id);
      setGroupes(groupes.filter((g) => g.id !== id));
    } catch (err) {
      console.error("Erreur suppression groupe :", err);
    }
  };

  const handleShowHistory = async (groupe: GroupeFrigorifique) => {
    setHistoryGroupe(groupe);
    const data = await getHistoriqueGroupeFrigorifique(groupe.id);
    setHistoriques(data);
    setShowHistoryModal(true);
  };

  if (loading) return <p>Chargement...</p>;

  return (
    <div className="groupes-container">
      <div className="groupes-header">
        <button className="btn-ajout" onClick={() => { setSelectedGroupe(null); setShowModal(true); }}>
          ➕ Ajouter Groupe
        </button>
      </div>

      <div className="groupes-stats">
        <div className="stat-card">
          <p>Total Groupes</p>
          <h3>{groupes.length}</h3>
        </div>
        <div className="stat-card">
          <p>En Service</p>
          <h3>{groupes.filter(g => g.etat).length}</h3>
        </div>
        <div className="stat-card">
          <p>Puissance Totale</p>
          <h3>{groupes.filter(g => g.etat).reduce((sum, g) => sum + g.puissance_frigorifique, 0)} kW</h3>
        </div>
      </div>

      <div className="groupes-grid">
        {groupes.map((g) => (
          <div key={g.id} className="groupe-card">
            <div className="groupe-header">
              <span className="badge-groupe">
                <FaCogs className="icon-badge" /> {g.nom}
              </span>
              {g.etat ? <Power className="icon-active" /> : <PowerOff className="icon-inactive" />}
            </div>

            <div className="card-line">
              <FaMapMarkerAlt className="icon-line" />
              {g.chambre_froide ? `${g.chambre_froide.nom} - ${g.chambre_froide.entrepot.nom}` : "Chambre inconnue"}
            </div>

            <p className="groupe-info"><strong>Compresseur :</strong> {g.marque_compresseur} {g.modele_compresseur}</p>
            <p className="groupe-info"><strong>Nº Série :</strong> {g.numero_serie}</p>
            <p className="groupe-info"><strong>Fluide :</strong> {g.fluide_frigorigene}</p>
            <p className="groupe-info"><strong>Puissance Frigorifique :</strong> {g.puissance_frigorifique} kW</p>
            <p className="groupe-info"><strong>Puissance Absorbée :</strong> {g.puissance_absorbee} kW</p>

            <div className="card-actions">
              <FaEye className="icon-eye" onClick={() => setViewGroupe(g)} />
              <FaEdit className="icon-edit" onClick={() => { setSelectedGroupe(g); setShowModal(true); }} />
              <FaTrash className="icon-trash" onClick={() => handleDelete(g.id)} />
              <FaHistory className="icon-history" onClick={() => handleShowHistory(g)} />
            </div>
          </div>
        ))}
      </div>

      {/* ✅ Modale création / modification */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedGroupe ? "Modifier Groupe Frigorifique" : "Créer Groupe Frigorifique"}</h3>
              <button className="btn-close" onClick={() => setShowModal(false)}><X /></button>
            </div>
            <form className="modal-body" onSubmit={handleSubmit}>
              <input type="text" name="nom" placeholder="Nom" defaultValue={selectedGroupe?.nom ?? ""} required />
              <input type="file" name="photo" accept="image/*" />

              {/* Condenseur */}
              <h4 className="section-title"><Wind className="icon-section" /> Condenseur</h4>
              <div className="section-condenseur">
                <input type="text" name="modele_condenseur" placeholder="Modèle Condenseur" defaultValue={selectedGroupe?.modele_condenseur ?? ""} />
                <input type="text" name="ventilo_condenseur" placeholder="Ventilo Condenseur" defaultValue={selectedGroupe?.ventilo_condenseur ?? ""} />
                <input type="text" name="diametre_aspiration_condenseur" placeholder="Ø Aspiration Condenseur" defaultValue={selectedGroupe?.diametre_aspiration_condenseur ?? ""} />
                <input type="text" name="diametre_liquide_condenseur" placeholder="Ø Liquide Condenseur" defaultValue={selectedGroupe?.diametre_liquide_condenseur ?? ""} />
              </div>

                            {/* Compresseur */}
              <h4 className="section-title"><Gauge className="icon-section" /> Compresseur</h4>
              <div className="section-condenseur">
                <input type="text" name="marque_compresseur" placeholder="Marque Compresseur"
                  defaultValue={selectedGroupe?.marque_compresseur ?? ""} />
                <input type="text" name="modele_compresseur" placeholder="Modèle Compresseur"
                  defaultValue={selectedGroupe?.modele_compresseur ?? ""} />
                <input type="text" name="numero_serie" placeholder="Nº Série"
                  defaultValue={selectedGroupe?.numero_serie ?? ""} />
              </div>

              {/* Intensités */}
              <h4 className="section-title"><Zap className="icon-section" /> Intensités</h4>
              <div className="section-intensites">
                <input type="text" name="intensite_protection" placeholder="Intensité Protection"
                  defaultValue={selectedGroupe?.intensite_protection ?? ""} />
                <input type="text" name="intensite_regime" placeholder="Intensité Régime"
                  defaultValue={selectedGroupe?.intensite_regime ?? ""} />
                <input type="text" name="intensite_demarrage" placeholder="Intensité Démarrage"
                  defaultValue={selectedGroupe?.intensite_demarrage ?? ""} />
                <input type="number" name="puissance_absorbee" placeholder="Puissance Absorbée (kW)"
                  defaultValue={selectedGroupe?.puissance_absorbee?.toString() ?? ""} />
              </div>

              {/* Evaporateur */}
              <h4 className="section-title"><Snowflake className="icon-section" /> Evaporateur</h4>
              <div className="section-evaporateur">
                <input type="text" name="modele_evaporateur" placeholder="Modèle Evaporateur"
                  defaultValue={selectedGroupe?.modele_evaporateur ?? ""} />
                <input type="text" name="ventilo_evaporateur" placeholder="Ventilo Evaporateur"
                  defaultValue={selectedGroupe?.ventilo_evaporateur ?? ""} />
                <input type="text" name="gamme_resistance_degivrage" placeholder="Gamme Résistance Dégivrage"
                  defaultValue={selectedGroupe?.gamme_resistance_degivrage ?? ""} />
                <input type="text" name="intensite_resistance_degivrage" placeholder="Intensité Résistance Dégivrage"
                  defaultValue={selectedGroupe?.intensite_resistance_degivrage ?? ""} />
                <input type="text" name="nb_resistance_bac" placeholder="Résistances Bac"
                  defaultValue={selectedGroupe?.nb_resistance_bac ?? ""} />
                <input type="text" name="nb_resistance_batterie" placeholder="Résistances Batterie"
                  defaultValue={selectedGroupe?.nb_resistance_batterie ?? ""} />
                <input type="text" name="diametre_aspiration_evaporateur" placeholder="Ø Aspiration Evaporateur"
                  defaultValue={selectedGroupe?.diametre_aspiration_evaporateur ?? ""} />
                <input type="text" name="diametre_liquide_evaporateur" placeholder="Ø Liquide Evaporateur"
                  defaultValue={selectedGroupe?.diametre_liquide_evaporateur ?? ""} />
              </div>

              {/* Données Globales */}
              <h4 className="section-title">Données Globales</h4>
              <div className="section-condenseur">
                <input type="text" name="fluide_frigorigene" placeholder="Fluide Frigorigène"
                  defaultValue={selectedGroupe?.fluide_frigorigene ?? ""} />
                <input type="number" name="puissance_frigorifique" placeholder="Puissance Frigorifique (kW)"
                  defaultValue={selectedGroupe?.puissance_frigorifique?.toString() ?? ""} />
              </div>

              {/* Maintenance */}
              <h4 className="section-title"><Wrench className="icon-section" /> Maintenance</h4>
              <div className="section-maintenance-optimal">
                <label className="field-label">Date changement filtre liquide</label>
                <input type="date" name="date_changement_filtre_liquide"
                  defaultValue={selectedGroupe?.date_changement_filtre_liquide ?? ""} />
                <label className="field-label">Date changement filtre aspiration</label>
                <input type="date" name="date_changement_filtre_aspiration"
                  defaultValue={selectedGroupe?.date_changement_filtre_aspiration ?? ""} />
                <label className="field-label">Date vidange huile</label>
                <input type="date" name="date_vidange_huile"
                  defaultValue={selectedGroupe?.date_vidange_huile ?? ""} />
                <label className="field-label">Date entretien batterie chaude</label>
                <input type="date" name="date_entretien_batterie_chaude"
                  defaultValue={selectedGroupe?.date_entretien_batterie_chaude ?? ""} />
              </div>

              {/* Chambre froide */}
              <select name="chambre_froide_id" defaultValue={selectedGroupe?.chambre_froide?.id ?? ""} required>
                <option value=""> -- Sélectionner une chambre froide -- </option>
                {chambres.map((cf) => (
                  <option key={cf.id} value={cf.id}>
                    {cf.nom} ({cf.entrepot.nom})
                  </option>
                ))}
              </select>

              <label className="field-label">Date de mise en service</label>
              <input type="date" name="date_mise_en_service"
                defaultValue={selectedGroupe?.date_mise_en_service ?? ""} />

              <textarea name="description" placeholder="Description"
                defaultValue={selectedGroupe?.description ?? ""}></textarea>

              {/* case En service */}
              <label className="checkbox-label">
                En service
                <input type="checkbox" name="etat"
                  defaultChecked={selectedGroupe?.etat ?? false}
                  onChange={(e) => {
                    if (selectedGroupe) {
                      setSelectedGroupe({ ...selectedGroupe, etat: e.target.checked });
                    }
                  }} />
              </label>

              {/* champ commentaire facultatif si hors-service */}
              {selectedGroupe && !selectedGroupe.etat && (
                <textarea name="commentaire_hors_service"
                  placeholder="Raison de la mise hors-service (facultatif)"
                  defaultValue={selectedGroupe?.commentaire_hors_service ?? ""}></textarea>
              )}

              <div className="modal-actions">
                <button type="submit">{selectedGroupe ? "Mettre à jour" : "Créer"}</button>
                <button type="button" onClick={() => setShowModal(false)}>Annuler</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ✅ Modale détails */}
      {viewGroupe && (
        <div className="modal-overlay" onClick={() => setViewGroupe(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{viewGroupe.nom}</h3>
              <button className="btn-close" onClick={() => setViewGroupe(null)}><X /></button>
            </div>
            <div className="modal-body">
              <p className="groupe-location">
                {viewGroupe.chambre_froide
                  ? `${viewGroupe.chambre_froide.nom} - ${viewGroupe.chambre_froide.entrepot.nom}`
                  : "Chambre inconnue"}
              </p>

              {viewGroupe.photo && (
                <img src={viewGroupe.photo} alt="Photo du groupe" className="detail-photo" />
              )}

              <div className="section-condenseur">
                <h4 className="section-title"><Gauge className="icon-section" /> Compresseur</h4>
                <p><strong>Marque :</strong> {viewGroupe.marque_compresseur}</p>
                <p><strong>Modèle :</strong> {viewGroupe.modele_compresseur}</p>
                <p><strong>Nº Série :</strong> {viewGroupe.numero_serie}</p>
              </div>

              <div className="section-intensites">
                <h4 className="section-title"><Zap className="icon-section" /> Intensités</h4>
                <p><strong>Protection :</strong> {viewGroupe.intensite_protection}</p>
                <p><strong>Régime :</strong> {viewGroupe.intensite_regime}</p>
                <p><strong>Démarrage :</strong> {viewGroupe.intensite_demarrage}</p>
                <p><strong>Puissance absorbée :</strong> {viewGroupe.puissance_absorbee} kW</p>
              </div>

              <div className="section-evaporateur">
                <h4 className="section-title"><Snowflake className="icon-section" /> Evaporateur</h4>
                <p><strong>Modèle :</strong> {viewGroupe.modele_evaporateur}</p>
                <p><strong>Ventilo :</strong> {viewGroupe.ventilo_evaporateur}</p>
                <p><strong>Résistances Bac :</strong> {viewGroupe.nb_resistance_bac}</p>
                <p><strong>Résistances Batterie :</strong> {viewGroupe.nb_resistance_batterie}</p>
              </div>

              <div className="section-maintenance-optimal">
                <h4 className="section-title"><Wrench className="icon-section" /> Maintenance</h4>
                <p><strong>Date Changement Filtre Liquide :</strong> {viewGroupe.date_changement_filtre_liquide}</p>
                <p><strong>Date Changement Filtre Aspiration :</strong> {viewGroupe.date_changement_filtre_aspiration}</p>
                <p><strong>Date Vidange Huile :</strong> {viewGroupe.date_vidange_huile}</p>
                <p><strong>Date Entretien Batterie Chaude :</strong> {viewGroupe.date_entretien_batterie_chaude}</p>
              </div>

              <div className="section-condenseur">
                <h4 className="section-title">Données Globales</h4>
                <p><strong>Fluide :</strong> {viewGroupe.fluide_frigorigene}</p>
                <p><strong>Puissance Frigorifique :</strong> {viewGroupe.puissance_frigorifique} kW</p>
                <p><strong>Date Mise en Service :</strong> {viewGroupe.date_mise_en_service}</p>
              </div>

              {viewGroupe.description && (
                <div className="section-evaporateur">
                  <h4 className="section-title">Description</h4>
                  <p>{viewGroupe.description}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ✅ Modale historique */}
      {showHistoryModal && historyGroupe && (
        <div className="modal-overlay" onClick={() => setShowHistoryModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Historique des arrêts – {historyGroupe.nom}</h3>
              <button className="btn-close" onClick={() => setShowHistoryModal(false)}>
                <X />
              </button>
            </div>
            <div className="modal-body">
              {historiques.length > 0 ? (
                <ul>
                  {historiques.map((h) => (
                    <li key={h.id}>
                      <span className="history-text">
                        📅 {new Date(h.date_arret).toLocaleString()}
                        {h.commentaire && <> – 📝 {h.commentaire}</>}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="history-text">Aucun arrêt enregistré.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
